import React, { useState } from "react";
import { MapContainer, Circle, CircleMarker, useMapEvents } from "react-leaflet";
import { X, Siren } from "@phosphor-icons/react";
import MapBaseLayers from "./map/MapBaseLayers";
import MapSearchControl from "./map/MapSearchControl";
import { EVAC_LEVEL_META } from "./map/SheriffEvacLayer";
import { createEvacAlert } from "../lib/api";

function ClickPicker({ onPick }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

export default function EvacAlertFormModal({ open, onClose, onCreated, center }) {
  const [level, setLevel] = useState(3);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [radius, setRadius] = useState(5);
  const [sourceUrl, setSourceUrl] = useState("");
  const [point, setPoint] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;
  const meta = EVAC_LEVEL_META[level];

  const submit = async (e) => {
    e.preventDefault();
    if (!point) { setError("Click the map to place the alert center."); return; }
    if (!title.trim()) { setError("Area name is required."); return; }
    setSaving(true);
    setError(null);
    try {
      const created = await createEvacAlert({
        level,
        title: title.trim(),
        description: description.trim() || null,
        latitude: point[0],
        longitude: point[1],
        radius_mi: radius,
        source_url: sourceUrl.trim() || null,
      });
      onCreated?.(created);
      setTitle(""); setDescription(""); setSourceUrl(""); setPoint(null);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not post alert. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/70" data-testid="evac-form-modal">
      <div className="w-full max-w-2xl bg-tac-bg border border-tac-border rounded-sm max-h-[92vh] overflow-y-auto tac-scroll">
        <div className="flex items-center justify-between px-4 py-3 border-b border-tac-border">
          <div className="flex items-center gap-2">
            <Siren size={16} weight="fill" className="text-tac-danger" />
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-tac-text">
              Post Evacuation Alert
            </span>
          </div>
          <button data-testid="evac-form-close" onClick={onClose} className="text-tac-dim hover:text-tac-text">
            <X size={16} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="p-4 space-y-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Evacuation Level
            </div>
            <div className="flex gap-1.5">
              {[1, 2, 3].map((l) => {
                const m = EVAC_LEVEL_META[l];
                const active = level === l;
                return (
                  <button
                    key={l}
                    type="button"
                    data-testid={`evac-level-${l}`}
                    onClick={() => setLevel(l)}
                    className="flex-1 px-2 py-2 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors"
                    style={
                      active
                        ? { background: `${m.color}25`, borderColor: m.color, color: m.color }
                        : { background: "transparent", borderColor: "#28322D", color: "#979F91" }
                    }
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Click the map to place the alert center · radius {radius} mi (or search an address)
            </div>
            <div className="h-64 border rounded-sm overflow-hidden" style={{ borderColor: meta.color }}>
              <MapContainer
                center={center || [48.4, -119.5]}
                zoom={9}
                className="h-full w-full"
                style={{ background: "#0F1311" }}
              >
                <MapBaseLayers />
                <MapSearchControl
                  startOpen
                  showMarker={false}
                  zoomTo={13}
                  onSelect={(r) => setPoint([r.lat, r.lon])}
                />
                <ClickPicker onPick={setPoint} />
                {point && (
                  <>
                    <Circle
                      center={point}
                      radius={radius * 1609.34}
                      pathOptions={{ color: meta.color, weight: 2, fillColor: meta.color, fillOpacity: 0.15 }}
                    />
                    <CircleMarker
                      center={point}
                      radius={7}
                      pathOptions={{ color: "#0A0D0C", weight: 2, fillColor: meta.color, fillOpacity: 1 }}
                    />
                  </>
                )}
              </MapContainer>
            </div>
            <input
              data-testid="evac-radius-slider"
              type="range"
              min={1}
              max={30}
              step={1}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="w-full mt-2 accent-tac-primary"
            />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Area name *
            </div>
            <input
              data-testid="evac-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Loup Loup Canyon / Hwy 20 corridor"
              className="w-full bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[15px] text-tac-text placeholder:text-tac-faint outline-none"
            />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Details (optional)
            </div>
            <textarea
              data-testid="evac-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Boundaries, shelter locations, road closures…"
              className="w-full bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[15px] text-tac-text placeholder:text-tac-faint outline-none resize-y"
            />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Official source link (optional)
            </div>
            <input
              data-testid="evac-source-input"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://facebook.com/OkanoganCountyEM/…"
              className="w-full bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[15px] text-tac-text placeholder:text-tac-faint outline-none"
            />
          </div>

          {error && (
            <div data-testid="evac-form-error" className="text-[13px] text-tac-danger">{error}</div>
          )}

          <div className="flex items-center justify-between gap-3 pt-1">
            <div className="text-[12px] text-tac-faint">
              Subscribers near this area get an instant push notification.
            </div>
            <button
              data-testid="evac-form-submit"
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-white rounded-sm disabled:opacity-50 transition-colors"
              style={{ background: meta.color }}
            >
              {saving ? "Posting…" : "Post Alert"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
